import { useForm } from '@tanstack/react-form';
import { log } from 'evlog/client';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import { phoneValidator } from '@virtual-care/isomorphic';
import { Button, CardContent, Label } from '@virtual-care/shadcn';
import {
  AuthLayout,
  FieldMessage,
  PhoneInput,
  errorToFormIssue,
} from '@virtual-care/ui';

import { PhoneVerificationDialog } from '../components/phone-verification-dialog.tsx';
import { sendOtp } from '../firebase.tsx';

const phoneSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, { message: 'shared.validation.phoneRequired' }),
});

const phoneNumberValidator = phoneSchema.shape.phoneNumber.superRefine(
  phoneValidator({ validateEmpty: false }),
);

export default function SignIn() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Clear verify-phone session when returning to signin (handles back button)
  useEffect(() => {
    const vid = sessionStorage.getItem('verificationId');
    if (vid) {
      sessionStorage.removeItem(JSON.stringify(vid)); // phone number
    }
    sessionStorage.removeItem('verificationId');
    sessionStorage.removeItem('otpSentAt');
  }, []);

  const form = useForm({
    defaultValues: {
      phoneNumber: import.meta.env.DEV ? '+966512345678' : '+966',
    },
    validators: {
      onSubmit: phoneSchema,
      onSubmitAsync: async (props) => {
        try {
          log.info({ action: 'signin_submit' });
          await sendOtp(props.value.phoneNumber);
          await navigate('/verify-phone');
          return void 0;
        } catch (error) {
          log.error({ action: 'signin_submit', error });
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <AuthLayout
      center
      title={t('auth.signin.title')}
      subtitle={t('auth.signin.subtitle')}
    >
      <CardContent>
        <PhoneVerificationDialog phoneNumber={form.state.values.phoneNumber}>
          <form
            className="mt-6 space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await form.handleSubmit();
            }}
          >
            <form.Field
              name="phoneNumber"
              validators={{
                onBlurAsync: phoneNumberValidator,
              }}
              children={(field) => (
                <div className="grid w-full items-center gap-2">
                  <Label className="text-sm" htmlFor="phone">
                    {t('auth.common.phone')}
                  </Label>
                  <PhoneInput
                    id="phone"
                    dir="ltr"
                    className="rtl:flex-row-reverse"
                    value={field.state.value}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                    international
                    defaultCountry="SA"
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />

            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <Button
                  id="signin-submit-btn"
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full cursor-pointer"
                >
                  {isSubmitting
                    ? t('auth.common.signingIn')
                    : t('auth.signin.title')}
                </Button>
              )}
            />
            <SignInFooter />
          </form>
        </PhoneVerificationDialog>
      </CardContent>
    </AuthLayout>
  );
}

function SignInFooter() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col space-y-2">
      <p className="text-muted-foreground text-sm">
        {t('auth.signin.noAccount')}&nbsp;
        <a href="/register" className="text-foreground">
          {t('auth.signin.createAccount')}
        </a>
      </p>
      <a
        href="/b2b-login"
        className="text-muted-foreground hover:text-foreground text-sm"
      >
        {t('auth.signin.b2bLogin')}
      </a>
    </div>
  );
}
